import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "../components/layout/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export default function AdminLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { email: "", password: "" } });

  const onSubmit = async (values) => {
    try {
      const user = await login(values.email.trim(), values.password);
      if (user.role !== "admin") {
        logout();
        toast.error("These credentials are not authorised for the admin panel.");
        navigate("/login");
        return;
      }
      toast.success("Signed in to the admin panel");
      navigate("/admin/dashboard", { replace: true });
    } catch (error) {
      toast.error(apiError(error, "Unable to sign in"));
    }
  };

  return (
    <AuthLayout
      kicker="Administrator"
      title="Admin panel sign-in"
      subtitle="Restricted access. All actions in this panel are recorded in the activity log."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div className="space-y-2">
          <Label htmlFor="admin-email">Admin email</Label>
          <Input
            id="admin-email"
            type="email"
            autoComplete="email"
            placeholder="admin@invextiing.com"
            {...register("email")}
            data-testid="admin-login-email-input"
          />
          {errors.email ? <p className="text-xs text-destructive">{errors.email.message}</p> : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="admin-password">Password</Label>
          <div className="relative">
            <Input
              id="admin-password"
              type={show ? "text" : "password"}
              autoComplete="current-password"
              className="pr-10"
              {...register("password")}
              data-testid="admin-login-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground transition-colors hover:text-foreground"
              aria-label={show ? "Hide password" : "Show password"}
              data-testid="admin-login-password-toggle"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password ? <p className="text-xs text-destructive">{errors.password.message}</p> : null}
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting}
          data-testid="admin-login-submit-button"
        >
          {isSubmitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ShieldCheck className="mr-2 h-4 w-4" />
          )}
          Sign in as admin
        </Button>

        <p className="text-center text-xs">
          <Link
            to="/login"
            className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            data-testid="admin-login-client-link"
          >
            Back to investor sign-in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
